import React, { useEffect } from 'react';
import {
  Hero,
  Faq,
  Benefits,
  PageHeader,
  PageFooter,
  Storefront,
} from '@spotify-internal/landing-page-library';
import { MastheadResponse } from '@spotify-internal/touchdown-api-clients/types/masthead-api';
import {
  HeroType,
  BenefitsType,
  StorefrontType,
  FaqType,
  ErrorType,
  SharingHeroType,
} from '../../server/types';
import I18nManager from '../I18nManager';
import '@spotify-internal/landing-page-library/esm/styles/masthead/index.css';
import { TextDirection } from '@spotify-internal/touchdown-api-clients/types/country-attributes';
import { usePublicRuntimeConfigValue } from '../../lib/config';
import { track } from '@spotify-internal/track-js';

type TrackJsConfig = {
  enabled: boolean;
  googleTagManagerId?: string;
};

export type SchedulerData = {
  metadata: {
    title: string;
    description: string;
    image: string;
  };
  components: Array<
    | FaqType
    | BenefitsType
    | StorefrontType
    | HeroType
    | ErrorType
    | SharingHeroType
  >;
};

export type BasePageProps = {
  masthead?: MastheadResponse;
  direction: TextDirection;
  language: string;
  market: string;
  oneTrust: boolean;
};

export function BasePage({
  basePageProps: { masthead, language, direction, market, oneTrust },
  schedulerData,
}: {
  basePageProps: BasePageProps;
  schedulerData: SchedulerData;
}) {
  const { enabled: trackJsEnabled, googleTagManagerId } =
    usePublicRuntimeConfigValue<TrackJsConfig>('analytics.trackJs');

  useEffect(() => {
    if (!trackJsEnabled) {
      return;
    }

    // To minimize page flicker, we manually install
    // Google Optimize outside of our app bundle. See
    // _document.tsx.
    track({
      market,
      language,
      googleTagManagerId,
      oneTrust,
    });
  }, [oneTrust, googleTagManagerId, language, market, trackJsEnabled]);

  return (
    <I18nManager language={language.replace('_', '-')} direction={direction}>
      {masthead?.header && <PageHeader {...masthead.header} />}
      <main>
        {!!schedulerData.components &&
          schedulerData.components.map((component, i) => {
            switch (component.name) {
              case 'faq':
                return <Faq {...component.attributes} key={`faq-${i}`} />;
              case 'benefits':
                return (
                  <Benefits {...component.attributes} key={`benefits-${i}`} />
                );
              case 'storefront':
                return (
                  <Storefront
                    {...component.attributes}
                    key={`storefront-${i}`}
                  />
                );
              case 'error':
                return <Hero {...component.attributes} key={`error-${i}`} />;
              case 'hero':
                return <Hero {...component.attributes} key={`hero-${i}`} />;
              case 'sharing-hero':
                return (
                  <Hero {...component.attributes} key={`sharing-hero-${i}`} />
                );
              default:
                return <></>;
            }
          })}
      </main>
      {masthead?.footer && <PageFooter {...masthead.footer} />}
    </I18nManager>
  );
}

export default BasePage;
